import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Check, Star, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { StripeService } from '@/services/stripe-service';

interface PostSignupUpgradeProps {
  intendedPlan?: string;
  workspaceId?: string;
  workspaceSlug?: string;
  userEmail?: string;
}

export const PostSignupUpgrade: React.FC<PostSignupUpgradeProps> = ({
  intendedPlan,
  workspaceId,
  workspaceSlug,
  userEmail
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleUpgrade = async (planType: string) => {
    if (!workspaceId || !workspaceSlug || !userEmail) {
      setError('Missing workspace information. Please try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const sessionId = await StripeService.createCheckoutSession({
        planType,
        workspaceId,
        workspaceSlug,
        customerEmail: userEmail,
        purchaseType: 'subscription'
      });

      // Redirect to Stripe checkout
      await StripeService.redirectToCheckout(sessionId);
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError('Unable to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // Clear the intended plan from session storage
    sessionStorage.removeItem('intendedPlan');
    navigate('/');
  };

  const getPlanDetails = (planType: string) => {
    switch (planType) {
      case 'basic':
        return {
          name: 'Basic Plan',
          price: '$49/month',
          icon: <Star className="w-6 h-6" />,
          features: [
            '3 subdomains',
            '10 users',
            '5GB storage',
            '10 data configurations',
            'AI credits included',
            'Priority support'
          ]
        };
      case 'premium':
        return {
          name: 'Premium Plan',
          price: '$249/month',
          icon: <Crown className="w-6 h-6" />,
          features: [
            'Custom DNS',
            'SSO integration',
            '50 users',
            '10GB storage',
            'Unlimited data configurations',
            'Advanced AI credits',
            'Priority support'
          ]
        };
      default:
        return null;
    }
  };

  const planDetails = intendedPlan ? getPlanDetails(intendedPlan) : null;

  if (!intendedPlan || !planDetails) {
    return null;
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center text-green-600">
          Welcome to WattFactory!
        </CardTitle>
        <CardDescription className="text-center">
          Your account has been created successfully. Would you like to upgrade to the {planDetails.name}?
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Plan Details */}
        <div className="p-4 border rounded-lg bg-muted/50">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              {planDetails.icon}
            </div>
            <div>
              <h3 className="font-semibold">{planDetails.name}</h3>
              <p className="text-sm text-muted-foreground">{planDetails.price}</p>
            </div>
          </div>
          <ul className="space-y-2">
            {planDetails.features.map((feature, index) => (
              <li key={index} className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-primary flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Button
            onClick={() => handleUpgrade(intendedPlan)}
            className="w-full"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting checkout...
              </>
            ) : (
              `Upgrade to ${planDetails.name}`
            )}
          </Button>
          
          <Button
            onClick={handleSkip}
            variant="outline"
            className="w-full"
          >
            Continue with Free Plan
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          You can upgrade anytime from your workspace settings.
        </p>
      </CardContent>
    </Card>
  );
}; 